import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Row,
  Col,
  Form,
  Select,
  Radio,
  Checkbox,
  Divider,
  Typography,
  Input,
  InputNumber,
} from 'antd';

import {
  listFieldTypes,
  listPlayerPositions,
  listPlayFrequencies,
  listDominantFoot,
  listPersonalInterests,
  listPurchaseFrquencies,
  listJerseySizes,
  listCitiesByState,
} from '../../services/catalogs';
import debounce from 'lodash/debounce';
import { Alert } from 'antd';

const { Title } = Typography;
const INTEREST_MIN = 3;

const PlayerProfile = () => {
  const [fieldTypes, setFieldTypes] = useState([]);
  const [positions, setPositions] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [dominantFeet, setDominantFeet] = useState([]);
  const [interests, setInterests] = useState([]);
  const [purchaseFrequencies, setPurchaseFrequencies] = useState([]);
  const [jerseySizes, setJerseySizes] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const form = Form.useFormInstance();
  const stateId = form.getFieldValue('stateId');
  
  useEffect(() => {
    listFieldTypes().then(setFieldTypes);
    listPlayerPositions().then(setPositions);
    listPlayFrequencies().then(setFrequencies);
    listDominantFoot().then(setDominantFeet);
    listPersonalInterests().then(setInterests);
    listPurchaseFrquencies().then(setPurchaseFrequencies);
    listJerseySizes().then(setJerseySizes);
    if (stateId) {
      listCitiesByState(stateId).then(setCities);
    }
  }, [stateId]);

  const fetchCities = useCallback(async (value) => {
      if (!value) return;

      setLoadingCities(true);

      try {
        await listCitiesByState(stateId, value).then(setCities);
      } finally {
        setLoadingCities(false);
      }
    }, [stateId]);


  const debouncedFetchCities = useMemo(
    () => debounce(fetchCities, 500),
    [fetchCities]
  );

  return (
    <>
      <Title level={5}>Ficha deportiva - Jugador</Title>
      {!stateId && (
        <Alert
          type="warning"
          message="Primero selecciona el estado en la pestaña de información general"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {/* Estado / Ciudad / Tipo cancha */}
      <Row gutter={16}>
        <Col md={8}>
              <Form.Item 
                label="Ciudad"
                name={['playerProfile', 'cityId']}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  loading={loadingCities}
                  filterOption={false}
                  onSearch={debouncedFetchCities}
                  notFoundContent={loadingCities ? 'Buscando...' : 'Sin resultados'}      
                  options={cities.map(s => ({
                    key: s.id,
                    value: s.id,
                    label: s.name,
                  }))}
                  placeholder="Selecciona una ciudad"
                  disabled={!stateId}
                />
              </Form.Item>
        </Col>
        <Col md={8}>
          <Form.Item
            label="Tipo de cancha"
            name={['playerProfile', 'fieldTypeId']}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecciona tipo"
              options={fieldTypes.map(f => ({
                key: f.id,
                value: f.id,
                label: f.name,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Posición */}
      <Form.Item
        label="Posición"
        name={['playerProfile', 'positionId']}
        rules={[{ required: true }]}
      >
        <Radio.Group>
          {positions.map(p => (
            <Radio key={p.id} value={p.id}>
              {p.name}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      {/* Frecuencia / Equipo */}
      <Row gutter={16}>
        <Col md={8}>
          <Form.Item
            label="Frecuencia de juego"
            name={['playerProfile', 'playFrequencyId']}
            rules={[{ required: true }]}
          >
            <Select
              placeholder="Selecciona frecuencia"
              options={frequencies.map(f => ({
                key: f.id,
                value: f.id,
                label: f.name,
              }))}
            />
          </Form.Item>
        </Col>

        <Col md={8}>
          <Form.Item
            label="Equipo"
            name={['playerProfile', 'teamName']}
          >
            <Input
              placeholder="Nombre del equipo"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Altura / Pie hábil */}
      <Row gutter={16}>
        <Col md={6}>
          <Form.Item
            label="Altura (cm)"
            name={['playerProfile', 'heightCm']}
            rules={[{ required: true }]}
          >
            <InputNumber min={50} max={250} style={{ width: '100%' }} />
          </Form.Item>
        </Col>

        <Col md={10}>
          <Form.Item
            label="Pie hábil"
            name={['playerProfile', 'dominantFootId']}
            rules={[{ required: true }]}
          >
            <Radio.Group>
              {dominantFeet.map(d => (
                <Radio key={d.id} value={d.id}>
                  {d.name}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
        <Col md={6}>
          <Form.Item
            label="Talla de tachones"
            name={['playerProfile', 'cleatsSize']}
            rules={[{ required: true }]}
          >
            <InputNumber
              min={20}
              max={50}
              style={{ width: '100%' }}
              placeholder="Ej. 26"
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      {/* Intereses */}
      <Title level={5}>Intereses personales</Title>

      <Form.Item
        name={['playerProfile', 'interests']}
        rules={[
          {
            validator: (_, value) =>
              value?.length >= INTEREST_MIN
                ? Promise.resolve()
                : Promise.reject(
                    new Error(`Selecciona al menos ${INTEREST_MIN} intereses`),
                  ),
          },
        ]}
      >
        <Checkbox.Group>
          <Row>
            {interests.map(i => (
              <Col span={8} key={i.id}>
                <Checkbox value={i.id}>{i.name}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Form.Item>

      {/* Frecuencia compra */}
      <Form.Item
        label="¿Con qué frecuencia compras online?"
        name={['playerProfile', 'purchaseFrequencyId']}
        rules={[{ required: true }]}
      >
        <Radio.Group>
          {purchaseFrequencies.map(p => (
            <Radio key={p.id} value={p.id}>
              {p.name}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      {/* Jersey */}
      <Form.Item
        label="Talla de Jersey"
        name={['playerProfile', 'jerseySizeId']}
        rules={[{ required: true }]}
      >
        <Radio.Group>
          {jerseySizes.map(j => (
            <Radio key={j.id} value={j.id}>
              {j.name}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>
      <Row gutter={16}>
        <Col md={6}>
          <Form.Item
            label="Talla de zapato"
            name={['playerProfile', 'shoeSize']}
            rules={[{ required: true }]}
          >
            <InputNumber
              min={20}
              max={50}
              style={{ width: '100%' }}
              placeholder="Ej. 27"
            />
          </Form.Item>
        </Col>
      </Row>

    </>
  );
};

export default PlayerProfile;
